import { defineComponent as _defineComponent } from 'vue'
import WizardStep from '@/components/wizard/WizardStep.vue';
import SubsidiariesSelect from '@/components/inputs/SubsidiariesSelect.vue';
import CustomersSelect from '@/components/inputs/CustomersSelect.vue';
import { isEmpty, isNil, Nullable } from '@/utils/misc';
import { Membership, MembershipType, User } from '@/models/user';
import { computed, reactive, watch } from 'vue';
import { currentUser } from '@/utils/permissionUtils';
import { useI18n } from '@/composables/i18n';
import useVuelidate from '@vuelidate/core';
import { required, withLocalizedValidators } from '@/utils/validation';

interface FormData {
  subsidiaryId: number | null;
  subsidiaryName: string;
  otherMemberships: Nullable<Membership>[];
}


/** @deprecated THIS COMPONENT IS FROZEN **/
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'ServiceMembershipWizardStep',
  props: {
    value: null,
    fixedSubsidiary: { type: Boolean, default: false }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ((e: 'input', user: User) => void), expose: any, slots: any, attrs: any }) {

const props = __props as { value: User, fixedSubsidiary: boolean };





const { t } = useI18n();

const formData: FormData = reactive({
  subsidiaryId: null,
  subsidiaryName: '',
  otherMemberships: [],
});

const EMPTY_SUBSIDIARY_MEMBERSHIP = Object.freeze({
  type: MembershipType.Subsidiary,
  id: null,
  name: null,
} as Nullable<Membership>);

const EMPTY_CUSTOMER_MEMBERSHIP = Object.freeze({
  type: MembershipType.Customer,
  id: null,
  name: null,
} as Nullable<Membership>);

const validationRules = computed(() => ({
  subsidiaryId: withLocalizedValidators('inputs.subsidiaries_select.label', {
    required,
  }),
  otherMemberships: {
    avoidDuplicateMainSubsidiary: (value: Membership[]) => {
      return !value
        .filter((x) => x.type === MembershipType.Subsidiary)
        .map((x) => x.id)
        .includes(formData.subsidiaryId!);
    },
    avoidDuplicateSubsidiaries: (value: Membership[]) => {
      const subsidiaryMemberships = value
        .filter((x) => x.type === MembershipType.Subsidiary)
        .map((x) => x.id);
      const uniqueMemberships = [...new Set(subsidiaryMemberships)];
      return uniqueMemberships.length === subsidiaryMemberships.length;
    },
    avoidDuplicateCustomers: (value: Membership[]) => {
      const subsidiaryMemberships = value
        .filter((x) => x.type === MembershipType.Customer)
        .map((x) => x.id);
      const uniqueMemberships = [...new Set(subsidiaryMemberships)];
      return uniqueMemberships.length === subsidiaryMemberships.length;
    },
    required: (value: Membership[]) => value.every((x) => !isNil(x.id)),
  },
}));

const v$ = useVuelidate(validationRules, formData);

function onLeave() {
  const modifiedUser = User.GetCopy(props.value);
  modifiedUser.subsidiaryId = formData.subsidiaryId;
  modifiedUser.subsidiaryName = formData.subsidiaryName;
  modifiedUser.otherMemberships = getValidMemberships();

  emit('input', modifiedUser);
}

function getValidMemberships() {
  return formData.otherMemberships.filter(
    (membership) => !isNil(membership.id),
  ) as Membership[];
}

function addSubsidiaryMembership() {
  formData.otherMemberships.push({ ...EMPTY_SUBSIDIARY_MEMBERSHIP });
  v$.value.otherMemberships.$touch();
}

function addCustomerMembership() {
  formData.otherMemberships.push({ ...EMPTY_CUSTOMER_MEMBERSHIP });
  v$.value.otherMemberships.$touch();
}

function removeMembership(index: number) {
  formData.otherMemberships.splice(index, 1);
  v$.value.otherMemberships.$touch();
}

watch(
  () => props.value,
  () => {
    formData.subsidiaryId = props.value.subsidiaryId;
    formData.subsidiaryName = props.value.subsidiaryName;
    formData.otherMemberships = [...props.value.otherMemberships];
  },
  { immediate: true },
);

const isCurrentUserSubsidiaryAdmin = computed(
  () => currentUser().isSubsidiaryAdmin,
);

return { __sfc: true,props, emit, t, formData, EMPTY_SUBSIDIARY_MEMBERSHIP, EMPTY_CUSTOMER_MEMBERSHIP, validationRules, v$, onLeave, getValidMemberships, addSubsidiaryMembership, addCustomerMembership, removeMembership, isCurrentUserSubsidiaryAdmin, WizardStep, SubsidiariesSelect, CustomersSelect, isEmpty, MembershipType }
}

})